const DEFAULT_HELD_LABEL = 'Funds held';
const DEFAULT_IN_DEBIT_LABEL = 'Trust account in debit';
/**
 * Convert the wire's debit-positive trust balance into funds held.
 *
 * The trust reads (`/trust-balance`, `/trust-statement`) serve the
 * `TRUST_CREDITORS` control account on the ledger's debit-positive convention:
 * a receipt into trust CREDITS the liability, so funds held for a client arrive
 * NEGATIVE and a client whose account is in debit arrives POSITIVE. Every
 * reader-facing surface wants the opposite orientation, so the flip happens
 * here and nowhere else.
 *
 * This exists as a named function rather than an inline `-x` at each call site
 * because the inline form is invisible to a reader and silently inverts a
 * compliance-relevant figure when a new surface forgets it: the bank-line
 * overdraw warning compares an entered amount against this value, so a missed
 * negation there either suppresses a real warning or fires a false one.
 *
 * The statement-feeds `position` block is already funds-held oriented at its
 * own named wire field, so surfaces reading that do NOT call this.
 */
export function toFundsHeldMinorUnits(debitPositiveMinorUnits) {
    return -debitPositiveMinorUnits;
}
/**
 * The single decision behind every trust-position surface: whether the account
 * is in debit, what to call it, which amount to show, and how to colour it.
 *
 * Every surface must call this rather than testing the sign itself - the Bill
 * statement page, the client detail header, the bank-line allocation row, and
 * the statement PDF's closing row alike. The four moves (test `< 0`, flip the
 * amount for display, swap the label, mark it as an error) have to happen
 * together or the figure misleads, and a surface that re-derives them can get
 * one right and another wrong. Keeping the boundary here also means the `=== 0`
 * case is decided in one place: zero is funds held, not an account in debit.
 *
 * `tone` is deliberately abstract rather than a class name or a colour, because
 * the two consumers render it differently (a Tailwind class on the page, a
 * React-PDF style on the document) and neither can use the other's vocabulary.
 *
 * Labels are supplied by the caller rather than selected by a flag, because
 * each surface words them differently for its own context, and a boolean at the
 * call site says nothing about which wording it picks.
 */
export function describeTrustPosition(fundsHeldMinorUnits, labels) {
    if (fundsHeldMinorUnits >= 0) {
        return {
            inDebit: false,
            label: labels?.held ?? DEFAULT_HELD_LABEL,
            displayMinorUnits: fundsHeldMinorUnits,
            tone: 'default',
        };
    }
    return {
        inDebit: true,
        label: labels?.inDebit ?? DEFAULT_IN_DEBIT_LABEL,
        displayMinorUnits: -fundsHeldMinorUnits,
        tone: 'error',
    };
}
/**
 * Which sides of a statement's BALANCE column carry a `Cr` / `Dr` suffix.
 *
 * On an AR statement debit is the expected direction (an amount the client
 * owes), so it stays plain and only the credit exception is marked. On a TRUST
 * statement the expectation is inverted - the firm normally HOLDS the client's
 * money - so a debit balance is both the exception and a breach of LPC Rule
 * 54.14.9, and leaving it unmarked lets the one figure that most needs to stand
 * out read as ordinary.
 *
 * Shared with `describeTrustPosition` above for the same reason: the statement
 * page and the exported PDF render the same table and must mark it the same
 * way. The numeric rule itself lives in `@packages/format`'s
 * `formatSignedBalance`, which takes the `BalanceMarking` this returns.
 */
export function balanceMarkingForStatementKind(kind) {
    switch (kind) {
        case 'TRUST':
            return 'both-sides';
        case 'BUSINESS':
        case 'COMBINED':
        case 'UNPAID_INVOICES':
            return 'credit-only';
    }
}
